<template>
  <Modal id="modal_change_image">
    <template #title>
      <template v-if="variant === 'avatar'">
        {{ $t("modal-change.changement-avatar") }}
      </template>
      <template v-else-if="variant === 'etablissement'">
        {{ $t("modal-change.changement-image-etablissement") }}
      </template>
    </template>
    <template #content>
      <h2>{{ $t("modal-change.appropriez-une-de-nos-propositions") }}</h2>
      <div class="flex-vcenter images-list">
        <label
          v-for="imgName in imgNames"
          :key="imgName"
        >
          <input
            v-model="checkedImg"
            type="radio"
            name="selections"
            :value="imgName"
            @change="changeCheckedImg()"
          />
          <img
            v-if="variant === 'avatar'"
            :src="require(`../../assets/media/avatars/${imgName}`)"
            :alt="$t('general.alt-avatar-du-compte')"
          />
          <img
            v-else-if="variant === 'etablissement'"
            :src="require(`../../assets/media/etablissements/${imgName}`)"
            :alt="$t('general.alt-image-etablissement')"
          />
        </label>
      </div>
      <h2>{{ $t("modal-change.ou-televersez-votre-propre-image") }}</h2>
      <div>
        <div
          v-if="!uploadedFile"
          class="input-file"
          :class="{ draged: draged, }"
          @drop="previewFile($event)"
          @dragleave="dragleave()"
          @dragover="dragover($event)"
        >
          <!-- On laisse un v-show="false" pour utiliser visuellement le ButtonClassic -->
          <input
            v-show="false"
            id="input_file"
            ref="file"
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            @change="previewFile($event)"
          />
          <label for="input_file">
            <ButtonClassic
              variant="solid"
              :label="draged ? $t('modal-change.lacher-image') : $t('modal-change.parcourir-vos-dossiers')"
              color="primary"
              icon="left"
              size="medium"
              @click="$refs.file.click()"
            >
              <template #left-icon>
                <UilFileUpload v-if="draged" />
                <UilFolderUpload v-else />
              </template>
            </ButtonClassic>
          </label>
        </div>
        <div
          v-else-if="uploadedFileURL"
          class="uploaded-file"
        >
          <Avatar :ext-img="uploadedFileURL" />
          <div class="cross">
            <ButtonClassic
              variant="simple"
              color="primary"
              icon="left"
              @click="deleteUploadedFile()"
            >
              <template #left-icon>
                <UilTimesCircle />
              </template>
            </ButtonClassic>
          </div>
        </div>
        <em
          class="text-small helper"
          v-html="$t('modal-change.format-jpeg-ou-png-poids-max-x-ko', { ko: poidsMaxImg, })"
        />
      </div>
    </template>
    <template #footer>
      <ButtonClassic
        variant="solid"
        :label="$t('modal-change.valider-image')"
        color="secondary"
        icon="left"
        :disabled="checkSelection"
        @click="$emit('validate-img', { checkedImg, uploadedFile, })"
      >
        <template #left-icon>
          <UilImageCheck />
        </template>
      </ButtonClassic>
    </template>
  </Modal>
</template>

<script>
import { defineComponent } from "vue";
import ButtonClassic from "../../components/buttons/ButtonClassic.vue";
import Avatar from "../../components/avatar/Avatar.vue";
import Modal from "../../components/modals/Modal.vue";

import config from "../../config";

import {
  UilImageCheck,
  UilFileUpload,
  UilFolderUpload,
  UilTimesCircle,
} from "@lde/vue-unicons";

/**
 * Modale affichée lorsque l'utilisateur souhaite changer une image (avatar, établissement...).
 */
export default defineComponent({
  name: "ModalChangeImage",
  components: {
    Modal,
    ButtonClassic,
    Avatar,
    UilImageCheck,
    UilFileUpload,
    UilFolderUpload,
    UilTimesCircle,
  },
  props: {
    /**
     * Change le contenu de la modale selon le contexte.
     */
    variant: {
      type: String,
      validator: (value) => ["avatar", "etablissement"].includes(value),
      default: "avatar",
    },
    /**
     * Définit les informations de l'utilisateur.
     */
    user: {
      type: Object,
      required: true,
    },
    /**
     * Définit les informations de l'organisme actuel.
     */
    organismeActuel: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    /**
     * Déclenché à l'enregistrement de l'avatar.
     * @param {Object} - Image sélectionnée ou uploaded.
     */
    "validate-img",
  ],
  data() {
    return {
      draged: false,
      checkedImg: null,
      uploadedFile: null,
      uploadedFileURL: null, // Pour faire la preview
    };
  },
  computed: {
    checkSelection() {
      return !(this.checkedImg || this.uploadedFile);
    },
    imgNames() {
      let listImg = [];

      if (this.variant === "avatar") {
        listImg = [
          "f_junior.svg",
          "f_junior_masque.svg",
          "f_senior.svg",
          "f_senior_masque.svg",
          "h_junior.svg",
          "h_junior_masque.svg",
          "h_senior.svg",
          "h_senior_masque.svg",
        ];
      } else if (this.variant === "etablissement") {
        listImg = [
          "primaire.svg",
          "college.svg",
          "lycee.svg",
        ];
      }

      return listImg;
    },
    poidsMaxImg() {
      return config.poidsMaxImg;
    },
  },
  mounted() {
    if (this.user?.avatar_from_library) {
      this.checkedImg = this.user.avatar_from_library;
    } else if (this.organismeActuel?.image_from_library) {
      this.checkedImg = this.organismeActuel.image_from_library;
    }
  },
  methods: {
    /**
     * Supprime le fichier uploadé.
     */
    deleteUploadedFile() {
      this.uploadedFile = null;
      this.uploadedFileURL = null;
    },
    /**
     * Supprime le fichier uploadé si on sélectionne une image proposée ensuite.
     */
    changeCheckedImg() {
      if (this.checkedImg && this.uploadedFile) {
        this.deleteUploadedFile();
      }
    },
    /**
     * Cache la modale.
     */
    hide() {
      this.$modal.hide("modal_change_image");
      this.deleteUploadedFile();
    },
    /**
     * Gestion de l'upload du fichier.
     * @param {Object} event Contient le fichier uploadé.
     */
    previewFile(event) {
      event.preventDefault();
      this.draged = false;
      this.checkedImg = null;
      let file = event.target.files || event.dataTransfer.files;
      // Vérification du nombre de fichier
      if (file && file.length === 1) {
        file = file[0];
      } else {
        this.displayError(this.$t("modal-change.selectionner-qu-un-seul-fichier"));
        return;
      }
      if (file) {
        // Vérifier le format
        if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
          // Une erreur de type
          this.displayError(this.$t("modal-change.fichier-doit-etre-au-format-jpg-ou-png"));
          return;
        }
        // Vérifie si vide
        if (file.size === 0) {
          // Une image ne peut être vide
          this.displayError(this.$t("modal-change.fichier-ne-peut-pas-etre-vide"));
          return;
        }
        const i = parseInt(Math.floor(Math.log(file.size) / Math.log(1024)), 10);
        const size = Math.round(file.size / (1024 ** i), 2);
        if (size > this.poidsMaxImg) {
          this.displayError(
            this.$t("modal-change.taille-du-fichier-doit-pas-depasser-x-ko", { ko: this.poidsMaxImg }),
          );
          return;
        }
        this.$toast.success({
          title: this.$t("modal-change.fichier-televerse-avec-succes"),
        });
        this.uploadedFile = file;
        this.uploadedFileURL = URL.createObjectURL(file);
      }
    },
    /**
     * Gestion lorsqu'on part du drag de l'image.
     */
    dragleave() {
      this.draged = false;
    },
    /**
     * Gestion lorsqu'on drag l'image.
     * @param {Object} event Événement natif de JS.
     */
    dragover(event) {
      event.preventDefault();
      this.draged = true;
    },
    /**
     * Affiche un toast d'erreur.
     * @param {String} title Titre du toast à afficher.
     */
    displayError(title) {
      this.$toast.error({ title });
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/modals/modal_change_image.scss";
</style>
